/**
 * PermissionSelect — komórka uprawnienia jako rozwijana lista (F24 Kawałek 1).
 *
 * Zastąpiła trzy ikonki-przełączniki: przy czterech możliwych stanach segmentowany przycisk
 * zmuszał do odgadywania, co znaczy która ikona, a najważniejszy stan („pod spodem są wyjątki")
 * w ogóle nie miał jak się pokazać.
 *
 * **Trzy wartości do wyboru** — Zezwalaj / Zabraniaj / {stan „bez własnej reguły"} — plus czwarty
 * stan, który tylko się POKAZUJE: gdy ustawienia pod tym wierszem NIE są jednolite, kontrolka
 * zostaje **PUSTA**. Puste pole czyta się jak „tu nie ma jednej odpowiedzi, zajrzyj niżej" i nie
 * udaje wartości, której nie da się wybrać. Szczegół („ile wyjątków siedzi w tej gałęzi") jest w
 * podpowiedzi pod kursorem.
 *
 * Przy stanie „bez własnej reguły" obok listy stoi mały znacznik wyniku (wolno / nie wolno), żeby
 * brak reguły nie wyglądał jak brak dostępu, a zaraz za nim znak zapytania otwierający rozpisanie
 * „z czego to wynika". W samej ramce zostaje wyłącznie nazwa stanu: dopisywanie do niej wyniku
 * („Zgodnie z rolą: zezwalaj") robiło z jednej komórki dwa zdania i powtarzało to, co znacznik
 * mówi jednym znakiem.
 *
 * Wszystko mieści się w JEDNEJ linii. Odnośnik „Skąd to wynika?" stał wcześniej pod kontrolką i
 * podwajał wysokość każdego wiersza — przy kilkudziesięciu wierszach tabela rozlewała się na
 * dwa ekrany i przestawała się czytać.
 *
 * ── Dlaczego to NIE jest `Dropdown` (Radix Select) ───────────────────────────────────────────
 * Bo ta kontrolka nie wybiera wartości, tylko **wydaje polecenie całej gałęzi**. Select z
 * przypiętą wartością milczy, gdy klikniesz pozycję już zaznaczoną — a tutaj to najczęstszy ruch:
 * wiersz zbiorczy pokazuje „Zezwalaj" wyliczone z tego, co jest niżej, więc ponowny wybór
 * „Zezwalaj" MA rozlać zezwolenie w dół. Na Selekcie taki klik nie robił nic i wyglądał jak
 * zepsuty ekran. Popover z trzema pozycjami zgłasza KAŻDY wybór, także powtórny.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { PermissionEffect } from '@saas/shared';
import { Check, ChevronDown, HelpCircle, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { cn } from '@/lib/utils';

/** Wartość komórki. `mixed` jest wyłącznie stanem WYŚWIETLANYM. */
export type PermissionCellValue = PermissionEffect | 'inherit' | 'mixed';

/** Co da się wybrać (bez `mixed`). */
export type PermissionCellChoice = PermissionEffect | 'inherit';

export interface PermissionSelectProps {
  value: PermissionCellValue;
  /**
   * Wynik, jaki wyjdzie przy braku własnej reguły — rysowany jako znacznik obok listy.
   * `null` = tego wiersza nie da się sprowadzić do jednego wyniku (wiersz zbiorczy nad całą
   * firmą, gdzie każda kartoteka może dziedziczyć co innego); znacznik wtedy nie powstaje.
   */
  inheritedResult: boolean | null;
  /**
   * Skąd płynie ten wynik: „rola Księgowa", „zespół Wdrożenia". Podawane przy podmiocie OSOBA —
   * tam pytanie „z roli czy z zespołu" jest pierwsze, jakie zadaje administrator.
   */
  inheritedFrom?: string | null;
  /**
   * Otwiera rozpisanie źródeł tej komórki. Bez tej funkcji odnośnik się nie rysuje — tak jest na
   * wierszu „Wszystko", nad którym nie ma żadnego podmiotu ani poziomu do pokazania.
   */
  onExplain?: () => void;
  /** Etykieta stanu „bez własnej reguły" — inna dla osoby, inna dla roli (patrz nagłówek). */
  inheritLabel: string;
  /** Ile jawnych wyjątków siedzi pod tym wierszem (podpowiedź przy stanie „Mieszane"). */
  overrideCount?: number;
  disabled?: boolean;
  onChange: (next: PermissionCellChoice) => void;
}

export function PermissionSelect({
  value,
  inheritedResult,
  inheritedFrom = null,
  inheritLabel,
  onExplain,
  overrideCount = 0,
  disabled = false,
  onChange,
}: PermissionSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const options: Array<{ value: PermissionCellChoice; label: string }> = [
    { value: 'allow', label: t('tenant.permissions.tri.allow') },
    { value: 'deny', label: t('tenant.permissions.tri.deny') },
    { value: 'inherit', label: inheritLabel },
  ];

  const isMixed = value === 'mixed';

  const effectLabel =
    inheritedResult === true ? t('tenant.permissions.tri.allow') : t('tenant.permissions.tri.deny');

  // Stan niejednolity = kontrolka PUSTA. Nie udajemy wartości, której nie da się wybrać.
  const triggerLabel = isMixed ? '' : (options.find((o) => o.value === value)?.label ?? '');

  const title = isMixed
    ? t('tenant.permissions.tri.mixedHint', { count: overrideCount })
    : value === 'inherit' && inheritedResult !== null
      ? inheritedFrom
        ? t('tenant.permissions.tri.inheritedFrom', {
            source: inheritedFrom,
            effect: effectLabel.toLowerCase(),
          })
        : inheritedResult
          ? t('tenant.permissions.tri.inheritedAllowed')
          : t('tenant.permissions.tri.inheritedDenied')
      : undefined;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-0.5" title={title}>
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
          <PopoverPrimitive.Trigger asChild>
            <Button
              variant="secondary"
              size="sm"
              disabled={disabled}
              aria-label={t('tenant.permissions.tri.groupLabel')}
              className="w-full min-w-[7rem] justify-between bg-bg-primary px-2 py-1 font-normal"
            >
              <span className="truncate">{triggerLabel}</span>
              <ChevronDown size={14} aria-hidden="true" className="flex-shrink-0 text-text-muted" />
            </Button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="start"
              sideOffset={4}
              className="z-[10001] min-w-[9rem] rounded-lg border border-border-primary bg-bg-elevated p-1 shadow-lg focus:outline-none"
            >
              {options.map((option) => (
                <Button
                  key={option.value}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start font-normal"
                  onClick={() => {
                    setOpen(false);
                    onChange(option.value);
                  }}
                >
                  <Check
                    size={14}
                    aria-hidden="true"
                    className={cn('flex-shrink-0', value !== option.value && 'invisible')}
                  />
                  {option.label}
                </Button>
              ))}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        {value === 'inherit' && inheritedResult !== null && (
          <span
            aria-hidden="true"
            className={cn(
              'flex-shrink-0',
              inheritedResult
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400',
            )}
          >
            {inheritedResult ? <Check size={14} /> : <X size={14} />}
          </span>
        )}
        {value === 'inherit' && inheritedResult !== null && onExplain && (
          <IconButton
            size="sm"
            className="flex-shrink-0 text-accent-600 dark:text-accent-400"
            aria-label={t('tenant.permissions.center.whySource')}
            title={t('tenant.permissions.center.whySource')}
            onClick={onExplain}
          >
            <HelpCircle size={14} aria-hidden="true" />
          </IconButton>
        )}
      </div>
    </div>
  );
}
